import AlphaEventDispatcher from './alpha-event-dispatcher'
import AlphaFilters from './alpha-filters'
import AlphaHelpers from './alpha-helpers'
import AlphaModule from './alpha-module'
import camelCase from 'lodash-es/camelCase'
import cloneDeep from 'lodash-es/cloneDeep'
import defaultModules from '../../alpha-modules/default'
import kebabCase from 'lodash-es/kebabCase'
import merge from 'lodash-es/merge'

export default class AlphaModuleManager extends AlphaEventDispatcher {
  constructor() {
    super()

    this._config = {}
    this._modules = {}
    this._listeners = {}
    this._modulesStates = {}
  }

  init() {
    AlphaHelpers.cleanupExpiredLocalStorageItems()

    this._loadConfig()
    this._loadDynamicScripts()
    this._loadModules()
    this.dispatchEvent('loaded')
  }

  refresh() {
    this._loadOptionalModulesFromDataAttributes()
    this.dispatchEvent('refreshed')
  }

  _loadConfig() {
    const alphaConfig = typeof window.alphaConfig !== 'undefined' ? window.alphaConfig : {}

    // load default config and potentially merge custom one from alphaConfig object
    this._config = this.getDefaultConfig()
    if (AlphaHelpers.objectHasAnyValues(alphaConfig)) {
      this.addConfig(alphaConfig)
    }
  }

  getConfig() {
    return cloneDeep(this._config)
  }

  getDefaultConfig() {
    return {
      siteName: 'Nematis',
      siteLogo: null,
      ajaxUrl: null,
      webpackBuildUrl: null,
      optionalModules: {},
      dynamicScripts: {
        block: [],
        menus: [],
        thirdParty: []
      },
      desktopBreakpoint: 'lg',
      adwordsConversions: {},
      locale: 'fr_FR',
      i18n: {},
      home: {},
      loader: {
        useLogo: false
      }
    }
  }

  addConfig(config) {
    this._config = merge(this._config, config)
  }

  /* DYNAMIC ASSETS */

  _loadDynamicScripts() {
    // these scripts are enqueued server-side, but due to how Webpack handles assets (through a namespace etc.)
    // we have to fake a load through Webpack, otherwise JavaScript code is never ran
    const config = this.getConfig()
    for (const blockName of config.dynamicScripts.block) {
      this._importBlockAssets(blockName)
    }

    for (const menuName of config.dynamicScripts.menus) {
      this._importMenuAssets(menuName)
    }

    for (const thirdPartyName of config.dynamicScripts.thirdParty) {
      this._importThirdPartyAssets(thirdPartyName)
    }
  }

  async _importBlockAssets(blockName) {
    const defaultImporter = () => import(/* webpackChunkName: "block-[request]" */ `../../blocks/${blockName}`)
    const res = await AlphaFilters.applyFiltersAsync('load.blocks', defaultImporter, blockName)
    if (typeof res === 'function') {
      res()
    }
  }

  async _importMenuAssets(menuName) {
    const defaultImporter = () => import(/* webpackChunkName: "menus-[request]" */ `../../menus/${menuName}`)
    const res = await AlphaFilters.applyFiltersAsync('load.menus', defaultImporter, menuName)
    if (typeof res === 'function') {
      res()
    }
  }

  async _importThirdPartyAssets(thirdPartyName) {
    const defaultImporter = () => import(/* webpackChunkName: "third-party-[request]" */ `../../third-party/${thirdPartyName}`)
    const res = await AlphaFilters.applyFiltersAsync('load.thirdParty', defaultImporter, thirdPartyName)
    if (typeof res === 'function') {
      res()
    }
  }

  /* MODULES HANDLING */

  _loadModules() {
    this._loadDefaultModules()
    this._loadOptionalModules()
  }

  _loadDefaultModules() {
    // load all modules from default/ folder
    // note: we're not using dynamic import here because it doesn't make much sense
    // since all these modules should be loaded everytime
    for (const moduleClass of Object.values(defaultModules)) {
      this.loadModule(moduleClass, true)
    }
  }

  _loadOptionalModules() {
    // import modules from config
    this._loadOptionalModulesFromConfig()

    // import modules from data-attrs
    this.addModuleEventListener(null, 'required-modules.injected', () => {
      this._loadOptionalModulesFromDataAttributes()
    }, true)
  }

  _loadOptionalModulesFromConfig() {
    const config = this.getConfig()
    for (const moduleFileName in config.optionalModules) {
      if (AlphaHelpers.objectHasOwnProperty(config.optionalModules, moduleFileName)) {
        // check if module isn't already loaded
        const moduleName = camelCase(moduleFileName)
        if (!this._canLoadModule(moduleName)) {
          continue
        }

        const moduleConfig = config.optionalModules[moduleFileName]
        this._importOptionalModule(moduleName, moduleFileName)
          .then(({
            default: moduleClass
          }) => this.loadModule(moduleClass, false, moduleConfig))
          .catch((error) => this._failedToImportModule(moduleName, error))
      }
    }
  }

  _loadOptionalModulesFromDataAttributes() {
    const requiredModulesAttrName = AlphaHelpers.getAlphaAttributeName('required-modules')
    const requiredModulesSelector = AlphaHelpers.getAlphaAttributeSelector('required-modules')
    const loadedModulesAttrName = AlphaHelpers.getAlphaAttributeName('loaded-modules')
    const loadedModulesSelector = AlphaHelpers.getAlphaAttributeSelector('loaded-modules')
    const watchedAttrName = AlphaHelpers.getAlphaAttributeName('watched')
    const watchedSelector = AlphaHelpers.getAlphaAttributeSelector('watched')

    // fast check: use querySelector to check if we have any element to parse (because querySelectorAll is slow)
    const elementsRequiringModules = AlphaHelpers.smartQuerySelectorAll(`${requiredModulesSelector}:not(${loadedModulesSelector}):not(${watchedSelector})`)
    if (elementsRequiringModules.length) {
      const allModulesToLoad = {}
      elementsRequiringModules.forEach((elRequiringModules) => {
        elRequiringModules.setAttribute(watchedAttrName, true)
        const modulesToLoad = AlphaHelpers.getParsedJsonAttribute(elRequiringModules, requiredModulesAttrName)
        if (modulesToLoad) {
          for (const moduleName in modulesToLoad) {
            // check if module isn't already loaded
            if (!this._canLoadModule(moduleName)) {
              continue
            }

            if (AlphaHelpers.objectHasOwnProperty(modulesToLoad, moduleName)) {
              // add module to load queue if necessary
              if (!AlphaHelpers.objectHasOwnProperty(allModulesToLoad, moduleName)) {
                allModulesToLoad[moduleName] = {
                  config: {},
                  elementsRequiring: []
                }
              }

              // add current element to calling elements (elements requiring this module)
              const currentModule = allModulesToLoad[moduleName]
              currentModule.elementsRequiring.push({
                element: elRequiringModules,
                modulesToLoad
              })

              // merge element's config with existing one if necessary
              const moduleConfig = modulesToLoad[moduleName]
              if (AlphaHelpers.objectHasAnyValues(moduleConfig)) {
                currentModule.config = merge(currentModule.config, moduleConfig)
              }
            }
          }
        }
      })

      // check if we have any module to load and load them
      if (AlphaHelpers.objectHasAnyValues(allModulesToLoad)) {
        for (const moduleName in allModulesToLoad) {
          if (AlphaHelpers.objectHasOwnProperty(allModulesToLoad, moduleName)) {
            const moduleFileName = kebabCase(moduleName)
            const currentModule = allModulesToLoad[moduleName]
            const elementsRequiringModule = currentModule.elementsRequiring

            // load module callback
            const maybeLoadModule = () => {
              this._importOptionalModule(moduleName, moduleFileName)
                .then(({
                  default: moduleClass
                }) => {
                  // add listener on load event and inform elements requiring this module that it has been loaded
                  this.addModuleEventListener(moduleName, 'loaded', () => {
                    const elementsRequiringModule = currentModule.elementsRequiring
                    elementsRequiringModule.forEach((elementInfo) => {
                      // get already loaded modules from data attr
                      const el = elementInfo.element
                      let loadedModules = []
                      const loadedModulesAttr = el.getAttribute(loadedModulesAttrName)
                      if (loadedModulesAttr) {
                        loadedModules = loadedModulesAttr.split(' ')
                      }

                      // mark this module as loaded for this element
                      loadedModules.push(moduleName)
                      el.setAttribute(loadedModulesAttrName, loadedModules)

                      // mark all modules are loaded for this element
                      const modulesToLoad = elementInfo.modulesToLoad
                      if (loadedModules.length === Object.keys(modulesToLoad).length) {
                        el.setAttribute(loadedModulesAttrName, 'all')
                      }
                    })
                  }, true)

                  // load module
                  const moduleConfig = currentModule.config
                  this.loadModule(moduleClass, false, moduleConfig)
                })
                .catch((error) => this._failedToImportModule(moduleName, error))
            }

            if ('IntersectionObserver' in window) {
              // IntersectionObserver is supported: trigger callback when any element is about to enter viewport
              const elementsObserver = new IntersectionObserver((entries, observer) => {
                const intersectingEntries = entries.filter((e) => e.isIntersecting)
                if (intersectingEntries.length) {
                  observer.disconnect()
                  maybeLoadModule()
                }
              }, {
                rootMargin: '150px 0px'
              })

              // start observing elements
              elementsRequiringModule.forEach((elementInfo) => {
                const el = elementInfo.element
                elementsObserver.observe(el)
              })
            } else {
              // IntersectionObserver is NOT supported: trigger callback right away
              maybeLoadModule()
            }
          }
        }
      }
    }
  }

  _importOptionalModule(moduleName, moduleFileName) {
    this._setModuleState(moduleName, AlphaHelpers.getModuleStates().stateImporting)

    // import module override if necessary
    if (this._moduleIsOverridden(moduleName)) {
      return this._importOptionalModuleOverride(moduleName)
    }

    return import(
      /* webpackChunkName: "module-[request]" */
      /* webpackPreload: true */
      `../../alpha-modules/optional/${moduleFileName}`)
  }

  _importOptionalModuleOverride(moduleName) {
    const moduleLoader = this._getModulesOverrides()[moduleName]()

    return moduleLoader
  }

  _moduleIsOverridden(moduleName) {
    const module = this.getModule(moduleName)
    if (module) {
      return module.isOverriddenModule()
    }

    return AlphaHelpers.objectHasOwnProperty(this._getModulesOverrides(), moduleName)
  }

  _getModulesOverrides() {
    return typeof window.alphaModulesOverrides !== 'undefined' ? window.alphaModulesOverrides : {}
  }

  _removeModuleOverride(moduleName) {
    delete this._getModulesOverrides()[moduleName]

    if (!Object.keys(this._getModulesOverrides()).length) {
      delete window.alphaModulesOverrides
    }
  }

  _failedToImportModule(moduleName, error = null) {
    this._setModuleState(moduleName, AlphaHelpers.getModuleStates().stateFailedToImport)

    if (error) {
      console.error(error)
    }
  }

  _canLoadModule(moduleName, strict = true) {
    const moduleState = this.getModuleState(moduleName, false)
    const moduleStates = AlphaHelpers.getModuleStates()
    let cond = moduleStates.stateNone === moduleState
    if (!strict) {
      cond = moduleState < moduleStates.stateLoaded
    }

    // check module flags to determine if this module can be loaded on current device
    const flags = AlphaHelpers.getModuleFlags()
    const moduleFlags = this._getModuleFlags(moduleName)
    const loadAnyDevice = AlphaHelpers.hasFlag(flags.loadAnyDevice, moduleFlags)
    if (!loadAnyDevice) {
      if (AlphaHelpers.hasFlag(flags.loadDesktopOnly, moduleFlags) && !AlphaHelpers.isDesktop()) {
        this._setModuleState(moduleName, moduleStates.stateFailedDesktopOnly)
        cond = false
      } else if (AlphaHelpers.hasFlag(flags.loadMobileOnly, moduleFlags) && !AlphaHelpers.isMobile()) {
        this._setModuleState(moduleName, moduleStates.stateFailedMobileOnly)
        cond = false
      }
    }

    return cond
  }

  async _maybeLoadModuleDependencies(moduleName, moduleObj) {
    const moduleStates = AlphaHelpers.getModuleStates()
    const moduleDependencies = moduleObj.getDependencies()
    if (!AlphaHelpers.objectHasAnyValues(moduleDependencies)) {
      return moduleObj
    }

    try {
      this._setModuleState(moduleObj.getName(), moduleStates.stateLoadingDependencies)
      for (const [dependencyName, dependencyConfig] of Object.entries(moduleDependencies)) {
        // first check if dependency can be loaded
        // _canLoadModule will set stateFailedDesktopOnly / stateFailedMobileOnly if necessary
        this._canLoadModule(dependencyName)
        const dependencyState = this.getModuleState(dependencyName, false)
        const dependencyStateString = AlphaHelpers.getModuleStateString(dependencyState)
        const dependencyFileName = kebabCase(dependencyName)
        switch (dependencyState) {
          case moduleStates.stateNone:
            // load dependency if necessary
            // eslint-disable-next-line no-await-in-loop
            await this._importOptionalModule(dependencyName, dependencyFileName)
              .then(({
                default: dependencyClass
              }) => {
                this.loadModule(dependencyClass, false, dependencyConfig)
              })
              .catch(() => {
                this._failedToImportModule(dependencyName)
                throw new Error(`can't import dependency ${dependencyName}`)
              })
            break
          case moduleStates.stateLoaded:
          case moduleStates.stateImporting:
          case moduleStates.stateLoadingDependencies:
            // just wait if dependency is already loading
            // eslint-disable-next-line no-await-in-loop
            await this.getOrAwaitModule(dependencyName)
            break
          default:
            // block loading if module depends on another module that can't be loaded
            throw new Error(`${dependencyName} can't be loaded (${dependencyStateString})`)
        }
      }
    } catch (error) {
      throw new Error(`Failed to load ${moduleName} dependencies with error: ${error.message}.`)
    }

    return moduleObj
  }

  async loadModule(moduleClass, isDefault = false, config = {}) {
    if (typeof moduleClass === 'function') {
      // eslint-disable-next-line new-cap
      let moduleObj = new moduleClass(isDefault, config)

      // eslint-disable-next-line no-prototype-builtins
      if (AlphaModule.prototype.isPrototypeOf(moduleObj)) {
        // check if module can be loaded
        // don't use strict mode here, because optional modules can have StateImporting
        const moduleName = moduleObj.getName()
        if (this._canLoadModule(moduleName, false)) {
          // try to load dependencies
          moduleObj = await this._maybeLoadModuleDependencies(moduleName, moduleObj)

          // mark module has overridden and remove its override loader
          const isOverride = this._moduleIsOverridden(moduleName)
          if (isOverride) {
            moduleObj._setOverridden()
            this._removeModuleOverride(moduleName)
          }

          // register module
          this._setModule(moduleName, moduleObj)
          moduleObj._onLoad()

          return this.getModule(moduleName)
        }
      }
    }

    return null
  }

  getModules() {
    return this._modules
  }

  getModule(moduleName) {
    const modules = this.getModules()
    if (AlphaHelpers.objectHasOwnProperty(modules, moduleName)) {
      return modules[moduleName]
    }

    return null
  }

  getOrAwaitModule(moduleName) {
    return new Promise((resolve) => {
      const module = this.getModule(moduleName)
      if (module) {
        resolve(module)
      } else {
        this.addModuleEventListener(moduleName, 'loaded', (detail) => resolve(detail.emitter), true)
      }
    })
  }

  getOrLoadModule(moduleName, isDefault = false) {
    const moduleFileName = kebabCase(moduleName)
    const moduleState = this.getModuleState(moduleName, isDefault)
    const moduleStates = AlphaHelpers.getModuleStates()
    let module = null

    return new Promise(async (resolve) => {
      switch (moduleState) {
        case moduleStates.stateNone:
          this._importOptionalModule(moduleName, moduleFileName)
            .then(async ({
              default: moduleClass
            }) => {
              module = await this.loadModule(moduleClass, isDefault)
              resolve(module)
            })
          break
        default:
          module = await this.getOrAwaitModule(moduleName)
          resolve(module)
      }
    })
  }

  _setModule(moduleName, moduleObj) {
    this._modules[moduleName] = moduleObj
  }

  getModulesStates() {
    return this._modulesStates
  }

  getModuleState(moduleName, toString = true) {
    let moduleState = AlphaHelpers.getModuleStates().stateNone
    const modulesStates = this.getModulesStates()
    if (AlphaHelpers.objectHasOwnProperty(modulesStates, moduleName)) {
      moduleState = modulesStates[moduleName]
    }

    return toString ? AlphaHelpers.getModuleStateString(moduleState) : moduleState
  }

  _setModuleState(moduleName, moduleState) {
    this._modulesStates[moduleName] = moduleState
  }

  _getModuleFlags(moduleName) {
    // module flags could have been defined in module class, but we define them here
    // that way, we can check them without having to loaded module class everytime
    const flags = AlphaHelpers.getModuleFlags()
    let moduleFlags = flags.loadAnyDevice
    switch (moduleName) {
      case 'exitOverlay':
        moduleFlags = flags.loadDesktopOnly
        break
      case 'wcFilterToggler':
        moduleFlags = flags.loadMobileOnly
        break
      default:
        break
    }

    return AlphaFilters.applyFilters(`module.${moduleName}.flags`, moduleFlags)
  }

  /* CUSTOM EVENTS SYSTEM */

  addModuleEventListener(moduleName, events, callback, once = false) {
    moduleName = this._sanitizeModuleName(moduleName)
    this._validateEventListenerParams(moduleName, events, callback)

    if (!Array.isArray(events)) {
      events = [events]
    }

    if (!AlphaHelpers.objectHasOwnProperty(this._listeners, moduleName)) {
      this._listeners[moduleName] = {}
    }

    const listenedModuleEvents = this._listeners[moduleName]
    for (const eventName of events) {
      if (!AlphaHelpers.objectHasOwnProperty(listenedModuleEvents, eventName)) {
        listenedModuleEvents[eventName] = []
      }

      // ensure that this listener isn't already registered
      const callbackIndex = this._findEventListenerIndexByCallback(moduleName, eventName, callback)
      if (callbackIndex === -1) {
        listenedModuleEvents[eventName].push({
          callback,
          once
        })
      }
    }
  }

  removeModuleEventListener(moduleName, events, callback) {
    moduleName = this._sanitizeModuleName(moduleName)
    this._validateEventListenerParams(moduleName, events, callback)

    if (!Array.isArray(events)) {
      events = [events]
    }

    // remove all matching listeners
    for (const eventName of events) {
      const callbackIndex = this._findEventListenerIndexByCallback(moduleName, eventName, callback)
      if (callbackIndex !== -1) {
        this._listeners[moduleName][eventName].splice(callbackIndex, 1)
      }

      this._maybeCleanupEventListeners(moduleName, events, callback)
    }
  }

  dispatchModuleEvent(moduleName, eventName, detail = {}) {
    moduleName = this._sanitizeModuleName(moduleName)
    this._validateEventListenerParams(moduleName, eventName, null, true)

    let value = null
    if (AlphaHelpers.objectHasOwnProperty(this._listeners, moduleName)) {
      const listenedModuleEvents = this._listeners[moduleName]
      for (const listenedEventName in listenedModuleEvents) {
        if (AlphaHelpers.objectHasOwnProperty(listenedModuleEvents, listenedEventName)) {
          const listenersToRemove = []
          switch (listenedEventName) {
            case '*':
            case eventName:
              // trigger corresponding callbacks (* is a wildcard for "any event from this module")
              for (const listener of listenedModuleEvents[listenedEventName]) {
                value = listener.callback(detail)
                if (listener.once) {
                  listenersToRemove.push(listener)
                }
              }

              // remove listeners after trigger if they are only supposed to trigger once
              if (listenersToRemove.length) {
                for (let i = listenersToRemove.length - 1; i >= 0; i--) {
                  this.removeModuleEventListener(moduleName, listenedEventName, listenersToRemove[i].callback)
                }
              }
              break
            default:
              break
          }
        }
      }
    }

    return value
  }

  _validateEventListenerParams(moduleName, events, callback, isDispatch = false) {
    if (typeof moduleName !== 'string' || !moduleName.length) {
      throw new Error('The moduleName parameter must be a non-empty string.')
    }

    if (typeof events !== 'string' && (isDispatch || !Array.isArray(events)) || !events.length) {
      if (isDispatch) {
        throw new Error('The eventName parameter must be a non-empty string for dispatchModuleEvent.')
      } else {
        throw new Error('The events parameter must be a non-empty string or an array.')
      }
    }

    if (Array.isArray(events) && events.some((eventName) => typeof eventName !== 'string' || !eventName.length)) {
      throw new Error('The array events parameter must only contain non-empty strings.')
    }

    if (isDispatch === false && typeof callback !== 'function') {
      throw new Error('The callback parameter must be a valid function.')
    }
  }

  _sanitizeModuleName(moduleName) {
    if (typeof moduleName === 'undefined' || moduleName === null) {
      moduleName = 'alpha'
    }

    return moduleName
  }

  _findEventListenerIndexByCallback(moduleName, eventName, callback) {
    if (AlphaHelpers.objectHasOwnProperty(this._listeners, moduleName)) {
      const listenedModuleEvents = this._listeners[moduleName]
      if (AlphaHelpers.objectHasOwnProperty(listenedModuleEvents, eventName)) {
        return listenedModuleEvents[eventName].findIndex((el) => el.callback === callback)
      }
    }

    return -1
  }

  _maybeCleanupEventListeners(moduleName, eventName) {
    if (AlphaHelpers.objectHasOwnProperty(this._listeners, moduleName)) {
      if (AlphaHelpers.objectHasOwnProperty(this._listeners[moduleName], eventName)) {
        // remove event array if it has no listener left
        if (!this._listeners[moduleName][eventName].length) {
          delete this._listeners[moduleName][eventName]

          // remove module object if it has no listened events left
          if (!AlphaHelpers.objectHasAnyValues(this._listeners[moduleName])) {
            delete this._listeners[moduleName]
          }
        }
      }
    }
  }
}
